import React, { useEffect, useRef, useState } from 'react';
import { isStandalone } from '../utils/pushPlatform';

const TRIGGER_PX = 70;   // pull this far (after resistance) to refresh
const MAX_PX = 110;

// When the app is installed on the home screen there's no browser pull-to-
// refresh, so add one: pull down from the very top of the page and let go to
// reload. In a normal browser tab the browser's own one is left alone.
const installed = isStandalone;

// Something on screen that scrolls on its own (a modal, a list) and isn't at its top
function insideScrolledArea(el) {
  for (let node = el; node && node !== document.body; node = node.parentElement) {
    if (node.scrollTop > 0) return true;
    if (node.getAttribute?.('aria-modal') === 'true' || node.classList?.contains('modal-overlay')) return true;
  }
  return false;
}

export default function PullToRefresh() {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const start = useRef(null);
  const distance = useRef(0);

  useEffect(() => {
    if (!installed()) return undefined;
    document.documentElement.classList.add('ptr-enabled');

    const onStart = (e) => {
      if (window.scrollY > 0 || e.touches.length !== 1 || insideScrolledArea(e.target)) { start.current = null; return; }
      start.current = e.touches[0].clientY;
      distance.current = 0;
    };
    const onMove = (e) => {
      if (start.current == null) return;
      const dy = e.touches[0].clientY - start.current;
      if (dy <= 0 || window.scrollY > 0) { distance.current = 0; setPull(0); return; }
      distance.current = Math.min(MAX_PX, dy * 0.5);
      setPull(distance.current);
    };
    const onEnd = () => {
      if (start.current == null) return;
      start.current = null;
      if (distance.current >= TRIGGER_PX) {
        setRefreshing(true);
        setPull(TRIGGER_PX);
        window.location.reload();
      } else {
        setPull(0);
      }
      distance.current = 0;
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
    return () => {
      document.documentElement.classList.remove('ptr-enabled');
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  if (!pull && !refreshing) return null;
  const ready = pull >= TRIGGER_PX;
  return (
    <div className="ptr" style={{ transform: `translateY(${pull - 44}px)` }} aria-live="polite">
      <span className={`ptr__spinner${refreshing ? ' ptr__spinner--spin' : ''}`} style={{ transform: refreshing ? undefined : `rotate(${pull * 3}deg)` }} aria-hidden="true">↻</span>
      <span className="ptr__label">{refreshing ? 'Refreshing…' : ready ? 'Let go to refresh' : 'Pull to refresh'}</span>
    </div>
  );
}
